import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Clock, Coins, TrendingUp, Zap } from 'lucide-react';
interface SlideSalesFocusProps {
  onContinue: () => void;
}
type Mode = 'today' | 'future';
const STAGES = [{
  label: 'Stage I',
  tagline: 'Pilot',
  revLabel: 'Low rev · Fast decision',
  dealSize: '€5–10K',
  timeToClose: '4–7 weeks',
  today: 85,
  future: 10,
  openQuotesTotal: '€110K',
  companies: ['Nestlé HS', 'Bayer', 'AstraZeneca', 'MSD', 'Sanofi', 'Novartis', 'Boehringer']
}, {
  label: 'Stage II',
  tagline: 'Expansion',
  revLabel: 'Mid rev · Considered',
  dealSize: '€25–60K',
  timeToClose: '3–6 months',
  today: 10,
  future: 20,
  openQuotesTotal: '€115K',
  companies: ['Solventum', 'Menarini', 'Roche', 'AbbVie', 'CSL Vifor']
}, {
  label: 'Stage III',
  tagline: 'Scale',
  revLabel: 'High rev · Strategic',
  dealSize: '€100K+',
  timeToClose: '4–8 months',
  today: 5,
  future: 30,
  openQuotesTotal: '€164K',
  companies: ['GSK', 'Leo Pharma']
}, {
  label: 'Stage IV',
  tagline: 'Enterprise',
  revLabel: 'High rev · Slow decision',
  dealSize: '€1M+',
  timeToClose: '12+ months',
  today: 0,
  future: 40,
  openQuotesTotal: '',
  companies: []
}] as const;
export function SlideSalesFocus({
  onContinue
}: SlideSalesFocusProps) {
  const [stage, setStage] = useState(0);
  const [mode, setMode] = useState<Mode>('today');
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 250);
    const t2 = setTimeout(() => setStage(2), 700);
    const t3 = setTimeout(() => setStage(3), 1200);
    const t4 = setTimeout(() => setStage(4), 1700);
    const t5 = setTimeout(() => setMode('future'), 2600);
    const t6 = setTimeout(() => setMode('today'), 4400);
    return () => {
      ;
      [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-7 md:px-16 md:py-9 max-w-[1400px] mx-auto overflow-y-auto" data-id="element-739" data-mp="13ffil452ykfp">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-740" data-mp="18f0hxfo5wusy">
        <p className="text-[#6C2BD9] text-xs md:text-sm font-bold uppercase tracking-[0.2em]" data-id="element-741" data-mp="yl1hcl3qlj34">
          Why we're raising
        </p>
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-slate-900 tracking-tight leading-tight mt-2" data-id="element-742" data-mp="opq4y9fswnyj">
          From <span className="text-[#6C2BD9]" data-id="element-743" data-mp="aqqpnse6hlf1">pilot</span> to{' '}
          <span className="text-[#6C2BD9]" data-id="element-744" data-mp="foa16kjhrk3a">enterprise penetration</span>
        </h2>
        <p className="text-slate-600 text-base md:text-lg mt-2 max-w-3xl" data-id="element-745" data-mp="fqd05u2mgnk5">
          Today we live off Stage I — fast revenue, but small. Investment gives
          us the liquidity to push Stage II clients into Stage III, where deals
          are 10× larger.
        </p>
      </motion.div>

      {/* Pilot → Enterprise arrow strip */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} className="mt-6 flex items-center gap-3" data-id="element-746" data-mp="3pmaawrpw3uoy">
        <span className="text-xs uppercase tracking-widest font-bold text-slate-500" data-id="element-747" data-mp="3e0uvm1v93bq">
          Pilot
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-slate-300 via-[#6C2BD9]/40 to-[#6C2BD9]" data-id="element-748" data-mp="h4e3zrqhuo2n" />
        <ArrowRight className="w-4 h-4 text-[#6C2BD9]" data-id="element-749" data-mp="c5t45tf5m6eq" />
        <span className="text-xs uppercase tracking-widest font-bold text-[#6C2BD9]" data-id="element-750" data-mp="r3li4h51ymqt">
          Enterprise Penetration
        </span>
      </motion.div>

      {/* Mode toggle */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 10
    }} className="mt-5 flex items-center justify-center" data-id="element-751" data-mp="m2xs9ysk8by6">
        <div className="inline-flex bg-slate-100 border border-slate-200 rounded-full p-1 relative" data-id="element-752" data-mp="1eyf2t404n2fq">
          <button onClick={() => setMode('today')} style={{
          color: mode === 'today' ? '#ffffff' : '#475569'
        }} className="relative z-10 px-5 md:px-6 py-2 rounded-full text-sm font-semibold transition-colors" data-id="element-753" data-mp="1jx0313fdpm46">
            <span style={{
            color: mode === 'today' ? '#ffffff' : '#475569'
          }} className="flex items-center gap-2" data-id="element-754" data-mp="w9iav6s7cm0o">
              <Zap className="w-3.5 h-3.5" style={{
              color: mode === 'today' ? '#ffffff' : '#475569'
            }} data-id="element-755" data-mp="158vmo63stmok" />
              Today · Liquidity mode
            </span>
          </button>
          <button onClick={() => setMode('future')} style={{
          color: mode === 'future' ? '#ffffff' : '#475569'
        }} className="relative z-10 px-5 md:px-6 py-2 rounded-full text-sm font-semibold transition-colors" data-id="element-756" data-mp="dg4t63di2c30">
            <span style={{
            color: mode === 'future' ? '#ffffff' : '#475569'
          }} className="flex items-center gap-2" data-id="element-757" data-mp="5205esf17x6u">
              <TrendingUp className="w-3.5 h-3.5" style={{
              color: mode === 'future' ? '#ffffff' : '#475569'
            }} data-id="element-758" data-mp="3s5ep1168n7vt" />
              With investment · Growth mode
            </span>
          </button>
          <motion.div layout transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className="absolute top-1 bottom-1 rounded-full bg-[#6C2BD9] shadow-md" style={{
          left: mode === 'today' ? 4 : '50%',
          right: mode === 'today' ? '50%' : 4
        }} data-id="element-759" data-mp="3di21x80s8u3b" />
        </div>
      </motion.div>

      {/* 4-stage grid */}
      <div className="flex-1 grid grid-cols-4 gap-3 md:gap-4 mt-6" data-id="element-760" data-mp="2usq72vaniduz">
        {STAGES.map((s, idx) => {
        const value = mode === 'today' ? s.today : s.future;
        const isPrimaryFocus = mode === 'today' && idx === 0 || mode === 'future' && (idx === 1 || idx === 2);
        return <motion.div key={s.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 20
        }} transition={{
          duration: 0.4,
          delay: idx * 0.08
        }} className={`relative rounded-2xl p-4 md:p-5 flex flex-col border transition-colors ${isPrimaryFocus ? 'bg-[#F5F0FF] border-[#6C2BD9]' : 'bg-white border-slate-200'}`} data-id="element-761" data-mp="38juzbcbow3va" data-mpi={idx}>
              <div className="flex items-baseline justify-between" data-id="element-762" data-mp="1kugpx8q0tmjl">
                <div data-id="element-763" data-mp="1ksngjlo0wo7w">
                  <p className="text-[10px] md:text-[11px] uppercase tracking-widest font-bold text-slate-500" data-id="element-764" data-mp="1fvn9b7fftff7">
                    {s.label}
                  </p>
                  <h4 className={`font-bold text-lg md:text-xl mt-0.5 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-900'}`} data-id="element-765" data-mp="1ujqhmngyd376">
                    {s.tagline}
                  </h4>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-766" data-mp="wx19rwzkjwde">
                  {idx === 0 ? 'I' : idx === 1 ? 'II' : idx === 2 ? 'III' : 'IV'}
                </span>
              </div>

              <div className="mt-4 flex items-end gap-1" data-id="element-767" data-mp="2i92r7og6zda0">
                <motion.span key={`${s.label}-${mode}`} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className={`text-4xl md:text-5xl font-bold tabular-nums tracking-tight ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-768" data-mp="fngrs3wfp0b4">
                  {value}
                </motion.span>
                <span className={`text-xl md:text-2xl font-bold mb-1 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-769" data-mp="18c4iiezychw2">
                  %
                </span>
                <span className={`ml-auto text-xs md:text-sm uppercase tracking-wider font-bold mb-1.5 ${isPrimaryFocus ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-770" data-mp="3a3r584ct9qmq">
                  of focus
                </span>
              </div>

              <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden" data-id="element-771" data-mp="1w9kdh99axr6w">
                <motion.div className={`h-full rounded-full ${isPrimaryFocus ? 'bg-[#6C2BD9]' : 'bg-slate-400'}`} animate={{
              width: `${value}%`
            }} transition={{
              type: 'spring',
              stiffness: 120,
              damping: 20
            }} data-id="element-772" data-mp="1rf6bup41qnts" />
              </div>

              <p className="text-xs text-slate-500 mt-3 leading-snug" data-id="element-773" data-mp="2b2m5umkp5cmr">
                {s.revLabel}
              </p>

              <div className="mt-4 pt-3 border-t border-slate-200 space-y-1.5" data-id="element-774" data-mp="216j6tpl55u41">
                <div className="flex items-center justify-between text-xs" data-id="element-775" data-mp="218ncabyov16f">
                  <span className="flex items-center gap-1.5 text-slate-500" data-id="element-776" data-mp="us1quv27hq8u">
                    <Coins className="w-3 h-3" data-id="element-777" data-mp="pqccp6joswgj" />
                    Order size
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-778" data-mp="kcr152dxw11k">{s.dealSize}</span>
                </div>
                <div className="flex items-center justify-between text-xs" data-id="element-779" data-mp="fe7cuvbvid61">
                  <span className="flex items-center gap-1.5 text-slate-500" data-id="element-780" data-mp="u20t8i8s9jpo">
                    <Clock className="w-3 h-3" data-id="element-781" data-mp="pbc439kksytp" />
                    Time to close
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-782" data-mp="131xoj2pohgtp">
                    {s.timeToClose}
                  </span>
                </div>
              </div>

              {s.companies.length > 0 && <div className="mt-3 pt-3 border-t border-slate-200" data-id="element-783" data-mp="1d0wnftkn5y3i">
                  <div className="flex flex-wrap gap-1.5" data-id="element-784" data-mp="181k0z9xomt1g">
                    {s.companies.map(c => <span key={c} className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${isPrimaryFocus ? 'bg-white border-[#6C2BD9]/30 text-[#6C2BD9]' : 'bg-slate-50 border-slate-200 text-slate-700'}`} data-id="element-785" data-mp="3erq82v9hl5id">
                        {c}
                      </span>)}
                  </div>
                </div>}

              {s.label === 'Stage IV' && <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-id="element-786" data-mp="1kjw59usgfhkr">
                  <div className="flex items-baseline justify-between" data-id="element-787" data-mp="1kht7gol28k5h">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500" data-id="element-788" data-mp="16itkdld74dbf">
                      Combined quotes
                    </p>
                    <p className="text-[11px] font-bold text-[#6C2BD9]" data-id="element-789" data-mp="wn07u1zh8jtn">
                      €389K
                    </p>
                  </div>
                  <div className="flex items-baseline justify-between" data-id="element-790" data-mp="11ljslz2c3pkf">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500" data-id="element-791" data-mp="2r1jb46ruercq">
                      Combined full potential
                    </p>
                    <p className="text-[11px] font-bold text-[#6C2BD9]" data-id="element-792" data-mp="2uiove8krbmy1">
                      €22.5M
                    </p>
                  </div>
                </div>}
            </motion.div>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 10
    }} className="mt-5 rounded-2xl border border-[#E4D7FF] bg-[#F5F0FF] px-5 py-4" data-id="element-793" data-mp="2kkhqwp7nmxcx">
        <AnimatePresence mode="wait" data-id="element-794" data-mp="38almc9aovqsu">
          <motion.div key={mode} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.3
        }} className="flex items-start gap-3" data-id="element-795" data-mp="38bn4exq4hdk2">
            <div className="w-8 h-8 rounded-lg bg-[#6C2BD9] text-white flex items-center justify-center flex-shrink-0" data-id="element-796" data-mp="3by08p2zkrat">
              {mode === 'today' ? <Zap className="w-4 h-4" data-id="element-797" data-mp="7djhsnqgttrc" /> : <TrendingUp className="w-4 h-4" data-id="element-798" data-mp="cc33qo5275jp" />}
            </div>
            <div className="flex-1" data-id="element-799" data-mp="hbyyrxvs3mn4">
              {mode === 'today' ? <>
                  <p className="text-sm font-bold text-slate-900" data-id="element-800" data-mp="masgy3m0g1tm">
                    Today, 85% of our sales effort goes to Stage I deals.
                  </p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-id="element-801" data-mp="qa17kzlr7jky">
                    Pilots close fast (4–7 weeks) and bring in cash, but they
                    cap at €5–10K. Out of necessity we chase what we can close —
                    leaving the bigger Stage II & III opportunities sitting in
                    the pipeline.
                  </p>
                </> : <>
                  <p className="text-sm font-bold text-slate-900" data-id="element-802" data-mp="26r8ev8vlyhkd">
                    With investment, 90% of effort shifts beyond Stage I.
                  </p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-id="element-803" data-mp="21s591m7t07bc">
                    Liquidity lets us run longer sales cycles and convert
                    existing relationships into Stage III (€100K+) and Stage IV
                    (€1M+) enterprise deals. Same accounts, 10× the deal size —
                    Pilot 10% · Expansion 20% · Scale 30% · Enterprise 40%.
                  </p>
                </>}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-4 flex justify-end" data-id="element-804" data-mp="1srqjubrpy82k"></motion.div>
    </div>;
}